import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { makeStyles } from '@material-ui/core/styles';
import { Divider } from '@material-ui/core';

import { SdmTypography } from 'components';
import { theme } from 'theme';
import CustomAccordion from './styledComponents/CustomAccordion';
import Controls from './additionalComponents/Controls';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import translations from 'utils/translations';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import SortableItem from './additionalComponents/SortableItem';

const useStyles = makeStyles({
  accordionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiCollapse-container.MuiCollapse-entered': {
      flex: 1,
      '& .MuiCollapse-wrapper': {
        height: '100%',
        '& .MuiCollapse-wrapperInner': {
          height: '100%',
          '& > div': {
            height: '100%',
            '& .MuiAccordionDetails-root': {
              height: '100%',
            },
          },
        },
      },
    },
  },

  accordionContentWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& .MuiPaper-root.MuiAccordion-root': {
      border: `0.5px solid ${theme.colors.primary.black}`,
      borderRadius: 0,
      boxShadow: 'none',
      marginTop: '10px',
    },
  },
});

const AdditionalAccordeon = ({
  control,
  additionalData,
  setValue,
  watch,
  setCustomErrors,
  getValues,
  fields,
  append,
  remove,
  langCode,
  productLangCode,
  setInfoDialog,
  isExhibitorGold = false,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const i18n = translations.productPage(intl);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  /**
   * @dnd-kit Logics
   */
  const [sortedItems, setSortedItems] = useState([]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const titleColor = isAccordionOpen
    ? theme.colors.primary.red
    : theme.colors.primary.black;
  const iconColor = isAccordionOpen ? 'primaryOpen' : 'primaryClosed';

  // Effects
  useEffect(() => {
    if (fields && fields.length !== sortedItems?.length) {
      if (!sortedItems.length) {
        // Siamo di ritorno dalla pagina, ci sono dei field ma non i sortedItems, vanno riallineati
        const _fields = [...fields];
        const sorted = _fields.sort((a, b) => a.position - b.position);
        setSortedItems(sorted.map((f) => f.id));
      } else {
        // Siamo gia nel drawer, va gestito l'add oppure il remove
        if (fields.length < sortedItems.length) {
          // Ne è stato cancellato uno dai Fields, va rimosso anche da sortedItems
          const filtered = [...sortedItems].filter((id) =>
            fields.find((f) => f.id === id)
          );
          setSortedItems(filtered);
        } else {
          // Gestisco anche la parte grafica del drag and drop: prendo l'ultimo valore dei fields e aggiungo il suo ID ai sortedItems
          // I Know how to destructure baby 😏
          const [{ id }] = fields.slice(-1);
          setSortedItems((prev) => [...prev, id]);
        }
      }
    }
  }, [fields]);

  // Functions
  const handleAddComponents = (e, name) => {
    if (!e.target.closest('.prevent-btn-info')) {
      const names = {};
      Object.entries(additionalData).map(([k, v]) => {
        if (k === name) {
          v.map((f) => (names[f.name] = ''));
        }
      });
      const input = {
        name,
        ...names,
        position: sortedItems.length,
      };
      append(input);
    }
  };

  const handleDeleteComponent = (id) => {
    const idx = fields.findIndex((f) => f.id === id);
    idx >= 0 && remove(idx);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id) return;
    if (active.id !== over.id) {
      const activeIdx = sortedItems.indexOf(active.id);
      const overIdx = sortedItems.indexOf(over.id);
      const array = arrayMove(sortedItems, activeIdx, overIdx);
      // Aggiorno il riferimento alle position anche nei Fields
      array.map((id, idx) => {
        const foundInFields = fields.findIndex((f) => f.id === id);
        setValue(`additionalContents[${foundInFields}].position`, idx);
      });
      setSortedItems(array);
    }
  };

  // Renders
  const renderSortableElement = ({ id }) => {
    const field = fields.find((f) => f.id === id);
    const fieldIdx = fields.findIndex((f) => f.id === id);
    return (
      field && (
        <SortableItem
          key={id}
          id={id}
          name={field.name}
          control={control}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          setCustomErrors={setCustomErrors}
          field={field}
          idx={fieldIdx}
          additionalData={additionalData}
          handleDelete={handleDeleteComponent}
          langCode={langCode}
          productLangCode={productLangCode}
          setInfoDialog={setInfoDialog}
        />
      )
    );
  };

  return (
    <CustomAccordion
      expandIconVariant={iconColor}
      onChange={setIsAccordionOpen}
      className={classes.accordionWrapper}
      accordionContentClassName={classes.accordionContentWrapper}
      style={{ flex: 1 }}
      title={
        <>
          <SdmTypography
            variant={'h3'}
            margin={0}
            style={{
              color: titleColor,
            }}
          >
            {i18n.titles.additionalAccordeon}
          </SdmTypography>
          <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
            {i18n.titles.additionalAccordeonSub}
          </SdmTypography>
        </>
      }
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedItems}
          strategy={verticalListSortingStrategy}
        >
          {sortedItems.map((id, idx) => renderSortableElement({ id, idx }))}
        </SortableContext>
      </DndContext>
      <div>
        <Divider
          style={{
            margin: '30px',
            backgroundColor: theme.colors.primary.black,
          }}
        />

        <Controls
          langCode={langCode}
          setInfoDialog={setInfoDialog}
          handleAddComponents={(e, name) => handleAddComponents(e, name)}
          buttons={
            additionalData && productLangCode !== 'en'
              ? Object.keys(additionalData)
              : []
          }
          fields={fields}
          isExhibitorGold={isExhibitorGold}
        />
      </div>
    </CustomAccordion>
  );
};

export default AdditionalAccordeon;
